import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Upload, Trash2 } from 'lucide-react';
import CameraCapture from './CameraCapture';
import { useImageViewer } from './ImageViewerProvider';

// ─────────────────────────────────────────────────────────────────────────────
// Reusable iOS-style photo capture/gallery modal. UI + camera + file picker +
// global viewer are handled here; persistence is delegated to the caller via
// `onAddFiles` / `onDeletePhoto`, and the photo list is controlled (`photos`).
// Mirrors the booking photo system so it can be dropped into any modal.
// ─────────────────────────────────────────────────────────────────────────────

export interface PhotoCaptureItem {
  id: string;
  url: string;
  name?: string;
}

interface PhotoCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  photos: PhotoCaptureItem[];
  /** Persist newly added files (camera or upload). Caller refreshes `photos`. */
  onAddFiles: (files: File[]) => Promise<void> | void;
  /** Remove a photo by id. Caller refreshes `photos`. */
  onDeletePhoto: (id: string) => Promise<void> | void;
}

async function blobUrlToFile(blobUrl: string, fileName: string): Promise<File> {
  const res = await fetch(blobUrl);
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
}

export default function PhotoCaptureModal({
  isOpen,
  onClose,
  title = 'Photos',
  subtitle,
  photos,
  onAddFiles,
  onDeletePhoto,
}: PhotoCaptureModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { open: openImageViewer } = useImageViewer();

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Mobile/iOS: open live camera immediately (expenses-style), with library upload on that screen
      if (typeof window !== 'undefined' && window.innerWidth < 640) {
        setShowCamera(true);
      }
    } else {
      setShowCamera(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 150);
  };

  const runAdd = async (files: File[]) => {
    if (!files.length) return;
    setUploading(true);
    try {
      await onAddFiles(files);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await runAdd(files);
  };

  const handleCameraPhotos = async (blobUrls: string[]) => {
    setShowCamera(false);
    if (!blobUrls.length) return;
    const files: File[] = [];
    for (let i = 0; i < blobUrls.length; i++) {
      try {
        files.push(await blobUrlToFile(blobUrls[i], `photo-${Date.now()}-${i + 1}.jpg`));
      } catch {
        /* ignore a bad blob */
      } finally {
        URL.revokeObjectURL(blobUrls[i]);
      }
    }
    await runAdd(files);
  };

  if (!isOpen) return null;

  const isMobile = window.innerWidth < 640;

  const modalContent = (
    <div
      className="fixed inset-0 overflow-y-auto"
      style={{
        zIndex: 100000,
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
      }}
    >
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${isAnimating ? 'bg-black/40' : 'bg-black/0'}`}
        style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
        onClick={handleClose}
      />

      <div
        className={`relative bg-white text-left overflow-hidden transform transition-all duration-200 ${
          isMobile
            ? `w-full h-full ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `inline-block align-middle rounded-[20px] shadow-2xl w-full max-w-4xl mx-auto my-8 ${
                isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
              }`
        }`}
        style={{
          maxHeight: isMobile ? '100vh' : '90vh',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-white border-b border-gray-200/60 ${isMobile ? 'px-3 py-2.5' : 'px-6 py-4'}`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-semibold text-gray-900 truncate`} style={{ letterSpacing: '-0.01em' }}>
                {title}
              </h3>
              {subtitle && <p className="mt-0.5 text-[13px] text-gray-600 truncate">{subtitle}</p>}
            </div>
            <button
              onClick={handleClose}
              className="h-8 w-8 flex items-center justify-center text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0 ml-2"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className={`${isMobile ? 'px-3 py-3' : 'px-6 py-5'} overflow-y-auto`}
          style={{ maxHeight: isMobile ? 'calc(100vh - 180px)' : 'calc(90vh - 200px)', WebkitOverflowScrolling: 'touch' }}
        >
          {/* Desktop: camera + library chooser. Mobile opens live camera directly (upload lives on camera UI). */}
          {isMobile ? (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                disabled={uploading || showCamera}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Camera className="h-5 w-5 mr-2" />
                <span className="text-[15px] font-medium">Add Photos</span>
              </button>
            </div>
          ) : (
            <div className="mb-6 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                disabled={uploading || showCamera}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Camera className="h-5 w-5 mr-2" />
                <span className="text-[15px] font-medium">{showCamera ? 'Camera Open...' : 'Open Camera'}</span>
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-[12px] hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Upload className="h-5 w-5 mr-2" />
                <span className="text-[15px] font-medium">Upload Photos</span>
              </button>
            </div>
          )}

          {photos.length > 0 ? (
            <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3 lg:grid-cols-4'} gap-2`}>
              {photos.map((photo, index) => (
                <div key={photo.id} className="relative group">
                  <div className="aspect-square rounded-[10px] overflow-hidden bg-gray-100">
                    <img
                      src={photo.url}
                      alt={photo.name || ''}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() =>
                        openImageViewer({
                          images: photos.map((p) => ({ src: p.url, caption: p.name })),
                          index,
                        })
                      }
                    />
                  </div>
                  <button
                    onClick={() => onDeletePhoto(photo.id)}
                    className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 active:opacity-100 active:bg-red-600 transition-all duration-150 hover:bg-red-600 active:scale-95 touch-manipulation shadow-sm"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center ${isMobile ? 'py-16' : 'py-12'} bg-gray-50/50 rounded-[12px] border border-gray-200/60`}>
              <Camera className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} text-gray-400 mx-auto mb-3`} />
              <p className="text-[13px] text-gray-600 font-medium">No photos yet</p>
              <p className="text-[11px] text-gray-500 mt-1">Tap to capture or upload photos</p>
            </div>
          )}

          {/* Library only — no capture attr (capture forced camera on iOS) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Footer */}
        <div className={`bg-white border-t border-gray-200/60 ${isMobile ? 'px-3 py-3' : 'px-6 py-4'}`}>
          <button
            type="button"
            onClick={handleClose}
            className={`w-full inline-flex justify-center rounded-[12px] px-4 py-3 bg-blue-600 text-white font-semibold hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none transition-all duration-150 touch-manipulation shadow-sm ${
              isMobile ? 'text-[15px]' : 'text-base'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Done {photos.length > 0 && `(${photos.length})`}
          </button>
        </div>

        <CameraCapture
          isOpen={showCamera}
          onClose={() => setShowCamera(false)}
          onPhotosCapture={handleCameraPhotos}
          title={title}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
